/**
 * Packaging OS — Admin console (MM7 foundation).
 *
 * Owner/admin operational surface: overview cards, review operations
 * (artifact/evidence/source-gap/knowledge queues), commercial requests
 * and support tickets. Functional and testable — final visual polish
 * is MM8 scope. Non-admin users get an honest blocked message; the
 * backend enforces `packaging.admin.console` regardless.
 */

import { useEffect, useState } from "react";
import {
  getAdminOverview,
  getAdminReviewQueue,
  getAdminEvidenceQueue,
  getAdminSourceGaps,
  getAdminKnowledge,
  getCommercialRequests,
  getSupportTickets,
  updateCommercialRequestStatus,
  type AdminOverview,
} from "../api/client";
import { Footer } from "../components/Footer";
import { PublicHeader } from "../components/PublicHeader";
import { TrustBanner } from "../components/TrustBanner";
import { strings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { useLocaleSwitch } from "../i18n/useLocaleSwitch";

type Tab = "overview" | "review" | "commercial" | "support";

type ConsoleState =
  | { kind: "loading" }
  | { kind: "blocked" }
  | { kind: "ready"; overview: AdminOverview };

export function AdminConsolePage(): JSX.Element {
  const [locale] = useLocale();
  const setLocale = useLocaleSwitch();
  const t = strings(locale);
  const [tab, setTab] = useState<Tab>("overview");
  const [state, setState] = useState<ConsoleState>({ kind: "loading" });
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await getAdminOverview();
      if (cancelled) return;
      if (result.outcome === "ok" && result.data) {
        setState({ kind: "ready", overview: result.data });
      } else {
        setState({ kind: "blocked" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state.kind !== "ready") return;
    let cancelled = false;
    void (async () => {
      let items: Record<string, unknown>[] = [];
      if (tab === "review") {
        const [queue, evidence, gaps, knowledge] = await Promise.all([
          getAdminReviewQueue(),
          getAdminEvidenceQueue(),
          getAdminSourceGaps(),
          getAdminKnowledge(),
        ]);
        items = [
          { section: t.adminCardReview, count: queue.data?.count ?? 0 },
          { section: "Evidence queue", count: evidence.data?.count ?? 0 },
          {
            section: t.adminCardGaps,
            count:
              ((gaps.data as { recent_open?: unknown[] })?.recent_open ?? []).length,
          },
          {
            section: t.adminCardKnowledge,
            count:
              ((knowledge.data as { recent_files?: unknown[] })?.recent_files ?? [])
                .length,
          },
        ];
      } else if (tab === "commercial") {
        const result = await getCommercialRequests();
        items = result.data?.items ?? [];
      } else if (tab === "support") {
        const result = await getSupportTickets();
        items = result.data?.items ?? [];
      }
      if (!cancelled) setRows(items);
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, state.kind, t]);

  async function markReviewed(requestId: string): Promise<void> {
    await updateCommercialRequestStatus(requestId, {
      status: "reviewed",
      note: "reviewed from admin console",
    });
    const result = await getCommercialRequests();
    setRows(result.data?.items ?? []);
  }

  return (
    <div className="po-page">
      <PublicHeader
        locale={locale}
        onLocaleChange={setLocale}
        switchLanguageLabel={t.switchLanguageLabel}
        brand={t.appName}
        navLinks={[{ to: "/app", label: t.appName }]}
      />

      <main className="po-page-main">
        <TrustBanner message={t.trustBannerShort} />
        <h1>{t.adminTitle}</h1>

        {state.kind === "loading" ? (
          <p role="status">…</p>
        ) : state.kind === "blocked" ? (
          <p role="alert" data-testid="admin-blocked">
            {t.adminBlockedMessage}
          </p>
        ) : (
          <>
            <nav className="po-tabs" aria-label={t.adminTitle}>
              {(
                [
                  ["overview", t.adminOverviewTab],
                  ["review", t.adminReviewOpsTab],
                  ["commercial", t.adminCommercialTab],
                  ["support", t.adminSupportTab],
                ] as Array<[Tab, string]>
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={tab === key ? "po-tab po-tab--active" : "po-tab"}
                  aria-pressed={tab === key}
                  onClick={() => setTab(key)}
                >
                  {label}
                </button>
              ))}
            </nav>

            {tab === "overview" ? (
              <section data-testid="admin-overview">
                <div className="po-capability-grid">
                  {(
                    [
                      [t.adminCardUsers, state.overview.cards.users],
                      [t.adminCardRuns, state.overview.cards.workflow_runs],
                      [t.adminCardReview,
                       state.overview.cards.artifacts_needing_review],
                      [t.adminCardGaps, state.overview.cards.open_source_gaps],
                      [t.adminCardKnowledge, state.overview.cards.knowledge_files],
                      [t.adminCardCost, state.overview.cards.total_cost_usd],
                      [t.adminCardRequests,
                       state.overview.cards.commercial_requests_new],
                    ] as Array<[string, number]>
                  ).map(([label, value]) => (
                    <article key={label} className="po-capability-card">
                      <h3>{String(value ?? 0)}</h3>
                      <p>{label}</p>
                    </article>
                  ))}
                </div>
                <p>
                  <span className="po-status-pill">
                    {t.adminAiModeLabel}: {state.overview.ai_readiness.mode}
                  </span>{" "}
                  <span className="po-status-pill">{t.adminBillingDisabled}</span>
                </p>
              </section>
            ) : null}

            {tab === "review" ? (
              <section data-testid="admin-review-ops">
                <ul>
                  {rows.map((row) => (
                    <li key={String(row.section)}>
                      {String(row.section)}: {String(row.count)}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {tab === "commercial" ? (
              <section data-testid="admin-commercial">
                <table className="po-table">
                  <thead>
                    <tr>
                      <th>{t.requestTypeLabel}</th>
                      <th>{t.requestNameLabel}</th>
                      <th>{t.requestCountryLabel}</th>
                      <th>{t.requestLanguageLabel}</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={String(row.id)}>
                        <td>{String(row.request_type)}</td>
                        <td>{String(row.requester_name)}</td>
                        <td>{String(row.country ?? "—")}</td>
                        <td>{String(row.preferred_language ?? "—")}</td>
                        <td>{String(row.status)}</td>
                        <td>
                          {row.status === "new" ? (
                            <button
                              type="button"
                              onClick={() => void markReviewed(String(row.id))}
                            >
                              ✓
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ) : null}

            {tab === "support" ? (
              <section data-testid="admin-support">
                <ul>
                  {rows.map((row) => (
                    <li key={String(row.id)}>
                      [{String(row.status)}] {String(row.category)} —{" "}
                      {String(row.subject)}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </main>

      <Footer copyright={t.copyrightLine} />
    </div>
  );
}
